import { useEffect, useState } from "react";
import useFetch from "../../global/hooks/useFetch";
import useMutation from "../../global/hooks/useMutation";

export const useCustomerTickets = () => {
  const { data, loading, error, refetch } = useFetch("/support/tickets/");

  return {
    tickets: data,
    loading,
    error,
    refetch,
  };
};

export const useCustomerTicketDetail = (ticketId) => {
  const [ticket, setTicket] = useState(null);

  const {
    data,
    loading,
    error: fetchError,
    refetch,
  } = useFetch(`/support/tickets/${ticketId}/`);

  useEffect(() => {
    if (data) {
      setTicket(data);
    }
  }, [data]);

  return {
    ticket,
    loading,
    error: fetchError,
    refetch,
  };
};

export const useCreateSupportTicket = () => {
  const {
    mutate: createTicketMutation,
    loading,
    error,
  } = useMutation("/support/tickets/", "POST");

  const createTicket = async (ticketData) => {
    try {
      const result = await createTicketMutation(ticketData);
      return result;
    } catch (error) {
      console.error("Error creating ticket:", error);
      throw error;
    }
  };

  return {
    createTicket,
    loading,
    error,
  };
};

export const useCustomerSupportStats = () => {
  const { data, loading, error, refetch } = useFetch("/support/stats/");

  return {
    stats: data,
    loading,
    error,
    refetch,
  };
};

export const useSupportTickets = (page = 1, search = "") => {
  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());
  if (search) {
    queryParams.append("search", search);
  }

  const url = `/support/admin/tickets/?${queryParams.toString()}`;
  const { data, loading, error, refetch } = useFetch(url);

  return {
    supportTickets: data?.results || [],
    pagination: {
      count: data?.count || 0,
      next: data?.next,
      previous: data?.previous,
      totalPages: data?.count ? Math.ceil(data.count / 20) : 0,
    },
    loading,
    error,
    refetch,
  };
};

export const useSupportTicket = (ticketId) => {
  const {
    data: ticket,
    loading,
    refetch,
  } = useFetch(`/support/admin/tickets/${ticketId}/`);

  const {
    mutate: updateTicketMutation,
    loading: updating,
    error: updateError,
  } = useMutation(`/support/admin/tickets/${ticketId}/`, "PATCH");

  const updateTicket = async (ticketData) => {
    const updatedTicket = await updateTicketMutation(ticketData);
    await refetch();
    return updatedTicket;
  };

  return {
    ticket,
    loading,
    updateTicket,
    updating,
    updateError,
    refetch,
  };
};

export const useUpdateSupportTicket = () => {
  const updateTicket = async (ticketId, ticketData) => {
    const token = localStorage.getItem("accessToken");
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

    const response = await fetch(
      `${apiUrl}/support/admin/tickets/${ticketId}/`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(ticketData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "Failed to update ticket");
    }

    return await response.json();
  };

  return {
    updateTicket,
  };
};

export const useSupportStats = () => {
  const { data, loading, error, refetch } = useFetch("/support/admin/stats/");

  return {
    stats: data || {},
    loading,
    error,
    refetch,
  };
};
